<template>
  <component
    :is="wrapperComp"
    v-bind="compProps"
    class="grid__block-content"
    :class="{ 'link-or-video': isLinkOrVideo }"
    :data-theme="themes[colorTheme ?? DataThemes._0]">
    <div v-if="link?.value?.url && youtubeIdUrl" class="block-video-wrapper">
      <div class="block-video">
        <iframe
          :src="`https://www.youtube.com/embed/${youtubeIdUrl}`"
          title="youtube-video"
          allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
          frameborder="0" />
      </div>
    </div>
    <div v-if="text && text.value && !youtubeIdUrl" class="grid__block-copy">
      <h3 v-if="title && title.value" v-bind="useEpiAttrs(title)">{{ title.value }}</h3>
      <BlocksWerkenbijContentArea :content="text" />
    </div>
    <figure
      v-if="image && image.value && image.value.url"
      class="grid__block-figure"
      :class="imageStyle">
      <img
        loading="lazy"
        :src="image.value.url + '?w=830'"
        :alt="image.value.alt ?? ''"
        :width="830" />
      <figcaption v-if="!text?.value && title?.value" class="grid__block-figcaption">
        <h3>{{ title?.value }}</h3>
      </figcaption>
    </figure>
    <ImagesSvg v-if="isLinkOrVideo" icon="arrow-right" color="white" :width="16" :height="24" />
  </component>
</template>

<script lang="ts" setup>
import type { ConcreteComponent } from 'vue';
import type { RowContentBlock } from '@base/types/models';
import { getVideoIdFromYoutubeString } from '@base/utils/getVideoId';
import { themes } from '@base/utils/theme';
import { DataThemes } from '@api';

const props = defineProps<{ content: RowContentBlock; imageStyle: string }>();
const { image, link, text, title, colorTheme } = props.content;

const wrapperComp = computed<ConcreteComponent | string>(() => {
  if (link?.value?.url !== undefined && link.value.url?.length) {
    return resolveComponent('NuxtLink');
  }

  return 'div';
});

const youtubeIdUrl = getVideoIdFromYoutubeString(props.content.link?.value?.url ?? '');

const isLinkOrVideo = computed(() => (wrapperComp.value as ConcreteComponent).name === 'NuxtLink');

const compProps = computed<Record<string, string> | null>(() => {
  if (isLinkOrVideo.value) {
    return { to: link?.value?.url ?? '' };
  }

  return null;
});
</script>

<style lang="scss" scoped>
.grid {
  &__block-content {
    display: flex;
    place-content: center center;
    color: var(--theme-color);
    background-color: var(--theme-bg-color);

    &:has(.block-video-wrapper) {
      align-items: center;
    }

    &.link-or-video {
      position: relative;
      transition: transform 0.15s ease-out;

      &:hover {
        transform: scale(1.02);
        text-decoration: none;
      }
    }

    svg {
      position: absolute;
      bottom: px(16);
      right: px(16);
      transition: left 0.2s;
      z-index: 20;
    }
  }

  &__block-figure {
    position: relative;
    display: block;
    flex: 1 1 100%;
    order: 1;
    min-height: px(296);
    overflow: hidden;

    &:first-child:last-child {
      border-radius: px(12);
    }

    img {
      position: absolute;
      inset: 0;
      object-fit: cover;
      width: 100%;
      height: 100%;
    }

    &.grid-block-icon {
      img {
        object-fit: contain;
      }
    }
  }

  &__block-figcaption {
    position: absolute;
    background: linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, rgba(0, 0, 0, 0.8) 100%);
    height: 50%;
    width: 100%;
    z-index: 10;
    bottom: 0;
    left: 0;
    right: 0;
    padding: px(24) px(16);

    h3 {
      position: absolute;
      overflow: hidden;
      font-size: px(18);
      line-height: px(22);
      bottom: px(24);
      max-height: px(38);
      max-width: calc(100% - px(48));
      color: inherit;

      @include media-breakpoint-up(lg) {
        font-size: px(22);
        line-height: px(26);
      }
    }
  }

  &__block-copy {
    order: 2;
    flex: 1 0 50%;
    padding: px(16);

    h1,
    h2,
    h3 {
      color: inherit;
    }

    :deep(p) {
      margin-top: 0;
    }

    @include media-breakpoint-up(lg) {
      padding: px(32) px(24);
      max-height: px(338);
      overflow: hidden;
    }
  }
}

.block-video-wrapper {
  width: 100%;

  .block-video {
    position: relative;

    &::before {
      content: '';
      display: block;
      width: 100%;
      padding-top: 56.25%;
    }

    iframe {
      position: absolute;
      width: 100%;
      height: 100%;
      inset: 0;
    }
  }
}
</style>
